import styled from '@emotion/styled';
import { GetServerSidePropsContext } from 'next/types';
import React from 'react';
import { HeadTags } from 'src/components/sections/head-tags/head-tags';
import ShipPageSections from 'src/components/sections/ship-sections/ship-sections';
import { getUGCPropsFromData } from 'src/components/sections/ugc/ugc.data';
import { ssrRedirectToErrorPage } from 'src/config/ssrRedirectToErrorPage';
import {
    ITabMenuItem,
    sectionNames,
    subMenuItems as subMenuItemsConfig,
} from 'src/constants/shipLanding';
import { DeviceContext } from 'src/contexts/device/device';
import { getShipPageData } from 'src/helpers/ship-page/getShipPageData';
import { IRefs, useElementOnScreen } from 'src/hooks/use-element-on-screen/use-element-on-screen';
import { useHandleSearchSubmit } from 'src/hooks/useHandleSearchSubmit';
import { StageMod } from 'src/modules/stage-mod';
import { ILandingPageProps } from '../../../interfaces/IPages';
import { IShip } from '../../../interfaces/ship/IShip';
import { TrustBadges } from '../../../src/components/sections/header/TrustBadges';
import { LocaleConfigContext } from '../../../src/contexts/locale-config/locale-config';
import { CallUsFooter } from '../../../src/components/molecules/call-us-footer/call-us-footer';

const getActiveSubMenuItems = (subMenuItemsConfig: ITabMenuItem[], shipData: IShip) => {
    const inactiveSubMenuItems: (typeof sectionNames)[keyof typeof sectionNames][] = [];
    const livePositionData = shipData?.livePosition;

    if (!livePositionData || !livePositionData.latitude || !livePositionData.longitude) {
        inactiveSubMenuItems.push(sectionNames.shipLivePosition);
    }
    if (!shipData?.decks) {
        inactiveSubMenuItems.push(sectionNames.cabin);
    }
    return subMenuItemsConfig.filter((item) => !inactiveSubMenuItems.includes(item.name));
};

const ShipLandingPage = (props: ILandingPageProps) => {
    const device = React.useContext(DeviceContext);
    const localeConfig = React.useContext(LocaleConfigContext);
    const [currentTabIndex, setCurrentTabIndex] = React.useState(0);

    const modulesData = props?.pageProps?.modulesData;
    const landingPage = props?.pageProps?.getLandingPage;
    const meta = props?.pageProps?.getLandingPage?.meta;

    const shipData = modulesData[`ship${landingPage.id}`] as IShip;
    const subMenuItems = getActiveSubMenuItems(subMenuItemsConfig, shipData);

    const hostUrl = props?.hostUrl;

    /**
     * `mobile = 115` | `tablet = 105` | `desktop = 105`
     */
    const HEADER_HEIGHT = device === 'mobile' ? 130 : 120;

    /**
     * Define `refs` for each _section_ / _module_ on the page, so we can track if it's on screen
     */
    const refs = React.useMemo(
        () =>
            subMenuItems.reduce((acc, value) => {
                acc[value.name] = React.createRef();
                return acc;
            }, {} as IRefs),
        [],
    );

    const { currentElementName } = useElementOnScreen({ refs, offset: HEADER_HEIGHT });

    React.useEffect(() => {
        const index = subMenuItems.findIndex((item) => item.name === currentElementName);
        setCurrentTabIndex(index > -1 ? index : currentTabIndex);
    }, [subMenuItems, currentTabIndex, currentElementName]);

    /**
     * Using `window.scrollTo` to scroll page to the specific section
     * `behavior = 'smooth'` is currently **not supported** from some browsers: _IE11 or Safari_
     *
     * More info here: `https://caniuse.com/#search=scrollto%20behavior`
     */
    const handleTabMenuClicked = React.useCallback(
        (selectedMenuItem?: ITabMenuItem) => {
            if (!selectedMenuItem) return;

            const ref = refs[selectedMenuItem.name];
            ref?.current &&
                // +5 is just a weird bug to always detect section that is on view
                // btw, IE11 does not support passing object to scrollTo, but we are using css
                // scroll-behavior: smooth
                window.scrollTo(0, ref.current.offsetTop - HEADER_HEIGHT + 5);
        },
        [refs, HEADER_HEIGHT],
    );

    const handleSubmit = useHandleSearchSubmit({
        id: landingPage.id,
        type: landingPage.type,
        title: landingPage.title,
    });

    const headTagsData = {
        ...meta,
        hostUrl,
        landingPage,
        modulesData,
        pageType: 'landingPage',
    };

    return (
        <>
            <HeadTags {...headTagsData} />

            <LandingPageContainer key="stage-container" ref={refs['stage']}>
                <StageMod
                    pageProps={props.pageProps}
                    ugcProps={getUGCPropsFromData({
                        ...props.pageProps,
                        ugcSlug: localeConfig.slugs.ugc,
                    })}
                    navItems={subMenuItems}
                    currentTabIndex={currentTabIndex}
                    onTabMenuSelect={(selectedMenuItem?: ITabMenuItem) =>
                        handleTabMenuClicked(selectedMenuItem)
                    }
                    onSearchSubmit={handleSubmit}
                    stageModuleType="searchStage"
                />
            </LandingPageContainer>

            {device === 'mobile' && (
                <BadgesContainer>
                    <TrustBadges portal={localeConfig.url} />
                </BadgesContainer>
            )}
            <CallUsFooter
                phoneNumber={localeConfig.phoneNumber}
                pageType={props.pageProps.pageType}
            />
            <ShipPageSections pageProps={props.pageProps} refs={refs} />
        </>
    );
};

export async function getServerSideProps(context: GetServerSidePropsContext) {
    const [, pageType, slug] = context.resolvedUrl.split('/');

    const pageData = await getShipPageData({ slug, pageType });

    if (!pageData) {
        return ssrRedirectToErrorPage;
    }

    const { landingPage, modulesData, configData } = pageData;
    return {
        props: {
            getLandingPage: landingPage,
            modulesData: modulesData,
            slug,
            pageType,
            globalConfigData: configData, // Used in the app as globalConfigData
        },
    };
}

const LandingPageContainer = styled.div`
    width: 100%;
`;

const BadgesContainer = styled.div`
    padding-top: 24px;
    background: #fff;
`;

export default ShipLandingPage;
